import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Input, Field, FieldLabel, FieldError } from '../components/ui';
import { PlatformCheckoutShell } from '../components/auth/PlatformCheckoutShell';
import { Building2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { fetchSubscriptionQuery } from '../queries/subscription';
import { buildSubscriptionStatusFromResponse } from '../utils/subscriptionStatus';
import {
  LEGAL_VERSIONS,
  getStatesForCountry,
  inferTimezoneFromLocation,
  isCountryEligibleForCourtSync,
  isStateRequiredForCountry,
  type AcademyOperatingProfileInput,
  type ClubOperatingProfileInput,
  type ClubPlanTier,
  type OrgOperationType,
  type SubscriptionTier,
  clubSportsFromAcademyOperatingProfile,
  clubLocationCountBandFromAcademyOperatingProfile,
  STARTER_BASELINE_LIMITATIONS,
} from '@court-sync/shared';
import {
  CountrySelectField,
  isCountrySelectionBlocked,
} from '../components/forms/CountrySelectField';
import { TimezoneSelectField } from '../components/forms/TimezoneSelectField';
import { useAuthStore } from '../store/auth';
import { AcademyOnboardingWizard } from '../components/onboarding/AcademyOnboardingWizard';
import {
  AcademyNeedsQuestionnaire,
  isQuestionnaireStepComplete,
  QUESTIONNAIRE_STEP_IDS,
  type QuestionnaireStepId,
} from '../components/onboarding/AcademyNeedsQuestionnaire';
import {
  ClubNeedsQuestionnaire,
  isClubQuestionnaireStepComplete,
  CLUB_QUESTIONNAIRE_STEP_IDS,
  type ClubQuestionnaireStepId,
} from '../components/onboarding/ClubNeedsQuestionnaire';
import { CombinedOnboardingPlanSelection } from '../components/onboarding/CombinedOnboardingPlanSelection';
import { OrgOperationTypeStep } from '../components/onboarding/OrgOperationTypeStep';
import {
  buildPlanRecommendation,
  buildClubPlanRecommendation,
  deriveOperatingProfileFlags,
  resolveOnboardingSelectedBundleTiers,
  resolveOnboardingSelectedPlanTier,
  resolveOnboardingSelectedClubPlanTier,
} from '../utils/planRecommendationUi';
import {
  mergeOnboardingDraft,
  persistSelectedPlanTier,
  persistSelectedClubPlanTier,
  readOnboardingDraft,
} from '../utils/onboardingDraft';
import { isAcademyDetailsStepComplete } from '../utils/academySetupStepComplete';
import { buildSetupStepsFromDraft } from '../utils/orgSetupSteps';
import {
  buildFinishSignupPath,
  clearCheckoutPlanPreselection,
  readStoredBillingInterval,
  SELECTED_BILLING_INTERVAL_STORAGE_KEY,
} from '../utils/billingInterval';
import {
  clearPricingSelection,
  pricingSelectionToOperationType,
  readPricingSelection,
} from '../utils/pricingSelection';
import { FastTrackSportsPicker, hasValidFastTrackSports } from '../components/onboarding/FastTrackSportsPicker';
import { PricingSelectedPlanCard } from '../components/onboarding/PricingSelectedPlanCard';
import { StarterLimitationsModal } from '../components/onboarding/StarterLimitationsModal';
import { userHasOrgEntity } from '../utils/platformLoginRedirect';
import { AuthService } from '../services/auth';
import { academyService } from '../services/academy';
import { tryAutoAddOwnerAsCoach } from '../lib/selfCoachPrompt';
import {
  trackOnboardingFastTrackChangePlan,
  trackOnboardingFastTrackStarted,
  trackOnboardingOperationTypeSelected,
  trackOnboardingPlanRecommendation,
  trackOnboardingPlanSelected,
  trackOnboardingQuestionnaireStep,
  trackStarterLimitationsAcknowledged,
} from '../utils/gtmTracking';

const setupFormSchema = z
  .object({
    academyName: z.string().max(100).optional().or(z.literal('')),
    clubName: z.string().max(255).optional().or(z.literal('')),
    country: z.string().length(2).optional().or(z.literal('')),
    stateOrProvince: z.string().max(10).optional().or(z.literal('')),
    timezone: z.string().max(64).optional().or(z.literal('')),
    termsAccepted: z.boolean().optional(),
    privacyAccepted: z.boolean().optional(),
    marketingEmailConsent: z.boolean().optional(),
  })
  .refine(
    (data) => !data.country?.trim() || isCountryEligibleForCourtSync(data.country),
    { message: 'This country is not supported for CourtSync subscriptions.', path: ['country'] }
  );

type SetupFormData = z.infer<typeof setupFormSchema>;

export default function AcademySetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, refreshUser } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [draft, setDraft] = useState(() => readOnboardingDraft());
  const [starterModalOpen, setStarterModalOpen] = useState(false);
  const [pendingSubmitValues, setPendingSubmitValues] = useState<SetupFormData | null>(null);
  const pricingFastTrackInitializedRef = useRef(false);

  const userEmail = location.state?.email ?? user?.email;
  const isSocialLogin = Boolean(location.state?.isSocialLogin);
  const orgOperationType = draft.orgOperationType ?? 'academy';
  const isPricingFastTrack = Boolean(draft.pricingFastTrack);
  const pricingBillingInterval = readStoredBillingInterval() ?? 'year';

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
    watch,
    control,
    getValues,
  } = useForm<SetupFormData>({
    resolver: zodResolver(setupFormSchema),
    defaultValues: {
      academyName: draft.academyName ?? '',
      clubName: draft.clubName ?? '',
      country: draft.country ?? '',
      stateOrProvince: draft.stateOrProvince ?? '',
      timezone: draft.academyTimezone ?? draft.clubTimezone ?? '',
      termsAccepted: false,
      privacyAccepted: false,
      marketingEmailConsent: false,
    },
  });

  const country = watch('country', '');
  const stateOrProvince = watch('stateOrProvince', '');
  const timezone = watch('timezone', '');
  const academyName = watch('academyName', '');
  const clubName = watch('clubName', '');
  const termsAccepted = watch('termsAccepted');
  const privacyAccepted = watch('privacyAccepted');
  const stateOptions = getStatesForCountry(country);
  const countryBlocked = isCountrySelectionBlocked(country);

  useEffect(() => {
    if (!isStateRequiredForCountry(country)) {
      setValue('stateOrProvince', '');
    }
  }, [country, setValue]);

  useEffect(() => {
    if (pricingFastTrackInitializedRef.current) return;
    const selection = readPricingSelection();
    if (!selection) return;

    pricingFastTrackInitializedRef.current = true;
    const operationType = pricingSelectionToOperationType(selection.productLine);
    const next = mergeOnboardingDraft({
      pricingFastTrack: true,
      pricingSelectionSource: 'pricing',
      orgOperationType: operationType,
      selectedPlanTier: selection.academyPlanTier,
      selectedClubPlanTier: selection.clubPlanTier,
      planTierManuallySelected: true,
      clubPlanTierManuallySelected: true,
      clubOperatingProfile:
        operationType !== 'academy'
          ? {
              ...(readOnboardingDraft().clubOperatingProfile ?? {}),
              offerings: ['court_hire', 'memberships', 'inventory_hire'],
            }
          : readOnboardingDraft().clubOperatingProfile,
    });
    setDraft(next);
    trackOnboardingFastTrackStarted({
      productLine: selection.productLine,
      academyTier: selection.academyPlanTier,
      clubTier: selection.clubPlanTier,
      billingInterval: selection.billingInterval,
    });
    trackOnboardingPlanSelected({
      productLine: selection.productLine,
      selectedTier:
        selection.productLine === 'club'
          ? selection.clubPlanTier ?? 'starter'
          : selection.productLine === 'both'
            ? `${selection.academyPlanTier}+${selection.clubPlanTier}`
            : selection.academyPlanTier ?? 'starter',
      source: 'pricing',
    });
  }, []);

  useEffect(() => {
    const checkUserSetup = async () => {
      if (!isAuthenticated) {
        navigate('/register');
        return;
      }

      try {
        await refreshUser();
        const refreshedUser = useAuthStore.getState().user;
        const hasOrgEntity = userHasOrgEntity(refreshedUser ?? {});

        if (hasOrgEntity) {
          const response = await fetchSubscriptionQuery({ force: true });
          const subscriptionStatus = buildSubscriptionStatusFromResponse(response);
          if (subscriptionStatus.hasActiveSubscription) {
            navigate('/dashboard', { replace: true });
            return;
          }
          navigate('/finish-signup', {
            state: {
              email: userEmail,
              message: 'Please complete your subscription setup.',
              isSocialLogin,
              orgOperationType: draft.orgOperationType,
              recommendedTier: draft.selectedPlanTier ?? draft.recommendedTier,
              recommendedClubTier: draft.selectedClubPlanTier ?? draft.recommendedClubTier,
              organizationId: draft.organizationId,
              orgSetupCompleted: true,
            },
          });
        }
      } catch {
        // Stay on setup page
      }
    };

    void checkUserSetup();
  }, [isAuthenticated, isSocialLogin, navigate, refreshUser, userEmail]);

  const operatingProfile = draft.operatingProfile;
  const clubOperatingProfile = draft.clubOperatingProfile ?? {};
  const allSetupSteps = useMemo(() => buildSetupStepsFromDraft(draft), [draft]);

  useEffect(() => {
    setCurrentStepIndex((index) => Math.min(index, Math.max(allSetupSteps.length - 1, 0)));
  }, [allSetupSteps.length]);

  const currentStep = allSetupSteps[currentStepIndex];

  const derivedProfile = useMemo(
    () => deriveOperatingProfileFlags(operatingProfile),
    [operatingProfile]
  );

  const recommendation = useMemo(
    () => buildPlanRecommendation(derivedProfile),
    [derivedProfile]
  );

  const clubRecommendation = useMemo(
    () => buildClubPlanRecommendation(clubOperatingProfile),
    [clubOperatingProfile]
  );

  const selectedPlanTier = useMemo(
    () => resolveOnboardingSelectedPlanTier(draft, recommendation),
    [draft, recommendation]
  );

  const selectedClubPlanTier = useMemo(
    () => resolveOnboardingSelectedClubPlanTier(draft, clubRecommendation),
    [draft, clubRecommendation]
  );

  const selectedBundleTiers = useMemo(
    () => resolveOnboardingSelectedBundleTiers(draft, recommendation),
    [draft, recommendation]
  );

  const updateDraft = useCallback((partial: Parameters<typeof mergeOnboardingDraft>[0]) => {
    const next = mergeOnboardingDraft(partial);
    setDraft(next);
  }, []);

  useEffect(() => {
    if (orgOperationType !== 'both') return;

    const syncedSports = clubSportsFromAcademyOperatingProfile(operatingProfile);
    const syncedLocationBand = clubLocationCountBandFromAcademyOperatingProfile(operatingProfile);
    if (!syncedSports && !syncedLocationBand) return;

    const sportsMatch =
      !syncedSports ||
      (clubOperatingProfile.sports &&
        syncedSports.length === clubOperatingProfile.sports.length &&
        syncedSports.every((sport, index) => {
          const current = clubOperatingProfile.sports![index];
          return (
            sport.sportId === current.sportId &&
            sport.isPrimary === current.isPrimary &&
            (sport.customLabel ?? '') === (current.customLabel ?? '')
          );
        }));
    const locationMatch =
      !syncedLocationBand || clubOperatingProfile.locationCountBand === syncedLocationBand;

    if (sportsMatch && locationMatch) return;

    updateDraft({
      clubOperatingProfile: {
        ...clubOperatingProfile,
        ...(syncedSports ? { sports: syncedSports } : {}),
        ...(syncedLocationBand ? { locationCountBand: syncedLocationBand } : {}),
      },
    });
  }, [clubOperatingProfile, operatingProfile, orgOperationType, updateDraft]);

  const handleProfileChange = useCallback(
    (profile: Partial<AcademyOperatingProfileInput>) => {
      updateDraft({ operatingProfile: profile });
    },
    [updateDraft]
  );

  const handleClubProfileChange = useCallback(
    (profile: Partial<ClubOperatingProfileInput>) => {
      updateDraft({ clubOperatingProfile: profile });
    },
    [updateDraft]
  );

  const handleOperationTypeChange = useCallback(
    (value: OrgOperationType) => {
      updateDraft({ orgOperationType: value });
      trackOnboardingOperationTypeSelected(value);
    },
    [updateDraft]
  );

  const handleChangePlanFromPricing = useCallback(() => {
    clearPricingSelection();
    clearCheckoutPlanPreselection();
    sessionStorage.removeItem(SELECTED_BILLING_INTERVAL_STORAGE_KEY);
    trackOnboardingFastTrackChangePlan();
    const reset = mergeOnboardingDraft({
      pricingFastTrack: false,
      pricingSelectionSource: undefined,
      planTierManuallySelected: false,
      clubPlanTierManuallySelected: false,
      selectedPlanTier: undefined,
      selectedClubPlanTier: undefined,
      starterLimitationsAcknowledged: false,
      currentStepId: 'operation-type',
      completedStepIds: [],
    });
    setDraft(reset);
    setCurrentStepIndex(0);
  }, []);

  const handleSelectPlanTier = useCallback(
    (tier: SubscriptionTier, options?: { starterAcknowledged?: boolean }) => {
      const acknowledgedAt =
        tier === 'starter' && options?.starterAcknowledged
          ? new Date().toISOString()
          : undefined;
      updateDraft({
        selectedPlanTier: tier,
        recommendedTier: recommendation.recommendedTier,
        planTierManuallySelected: true,
        starterLimitationsAcknowledged: tier === 'starter',
        operatingProfile: {
          ...operatingProfile,
          starterLimitationsAcknowledgedAt: acknowledgedAt,
        },
      });
      persistSelectedPlanTier(tier);
    },
    [operatingProfile, recommendation.recommendedTier, updateDraft]
  );

  const handleSelectClubPlanTier = useCallback(
    (tier: ClubPlanTier) => {
      updateDraft({
        selectedClubPlanTier: tier,
        recommendedClubTier: clubRecommendation.recommendedTier,
        clubPlanTierManuallySelected: true,
      });
      persistSelectedClubPlanTier(tier);
    },
    [clubRecommendation.recommendedTier, updateDraft]
  );

  const handleSelectBundle = useCallback(
    (academyTier: SubscriptionTier, clubTier: ClubPlanTier) => {
      handleSelectClubPlanTier(clubTier);
      handleSelectPlanTier(academyTier);
    },
    [handleSelectClubPlanTier, handleSelectPlanTier]
  );

  useEffect(() => {
    if (orgOperationType !== 'both' || currentStep?.id !== 'both-plans') return;
    if (draft.planTierManuallySelected || draft.clubPlanTierManuallySelected) return;

    const { academyTier, clubTier } = selectedBundleTiers;
    if (draft.selectedPlanTier === academyTier && draft.selectedClubPlanTier === clubTier) {
      return;
    }

    updateDraft({
      selectedPlanTier: academyTier,
      selectedClubPlanTier: clubTier,
      recommendedTier: recommendation.recommendedTier,
      recommendedClubTier: clubRecommendation.recommendedTier,
    });
  }, [
    clubRecommendation.recommendedTier,
    currentStep?.id,
    draft.clubPlanTierManuallySelected,
    draft.planTierManuallySelected,
    draft.selectedClubPlanTier,
    draft.selectedPlanTier,
    orgOperationType,
    recommendation.recommendedTier,
    selectedBundleTiers,
    updateDraft,
  ]);

  const isCurrentStepComplete = useMemo(() => {
    if (currentStep.id === 'operation-type') {
      return Boolean(draft.orgOperationType);
    }
    if (currentStep.id === 'academy-details') {
      const detailsComplete = isAcademyDetailsStepComplete({
        academyName,
        timezone,
        requireTimezone: true,
        termsAccepted,
        privacyAccepted,
        isSocialLogin: isSocialLogin && orgOperationType !== 'club',
        countryBlocked,
      });
      if (!detailsComplete) return false;
      if (!isPricingFastTrack) return true;
      return hasValidFastTrackSports(orgOperationType, operatingProfile, clubOperatingProfile);
    }
    if (currentStep.id === 'club-details') {
      const nameOk = orgOperationType === 'both' ? true : Boolean(clubName?.trim());
      const tzOk = Boolean(timezone?.trim());
      const countryOk = orgOperationType === 'both' ? true : Boolean(country?.trim()) && !countryBlocked;
      const legalOk = !isSocialLogin || (termsAccepted && privacyAccepted);
      if (!(nameOk && tzOk && countryOk && legalOk)) return false;
      if (!isPricingFastTrack) return true;
      return hasValidFastTrackSports(orgOperationType, operatingProfile, clubOperatingProfile);
    }
    if (currentStep.id === 'plan') {
      return orgOperationType === 'club'
        ? Boolean(selectedClubPlanTier)
        : Boolean(selectedPlanTier);
    }
    if (currentStep.id === 'both-plans') {
      return Boolean(selectedBundleTiers.academyTier && selectedBundleTiers.clubTier);
    }
    if (CLUB_QUESTIONNAIRE_STEP_IDS.includes(currentStep.id as ClubQuestionnaireStepId)) {
      return isClubQuestionnaireStepComplete(
        currentStep.id as ClubQuestionnaireStepId,
        clubOperatingProfile
      );
    }
    if (QUESTIONNAIRE_STEP_IDS.includes(currentStep.id as QuestionnaireStepId)) {
      return isQuestionnaireStepComplete(currentStep.id as QuestionnaireStepId, operatingProfile);
    }
    return false;
  }, [
    academyName,
    clubName,
    clubOperatingProfile,
    country,
    countryBlocked,
    currentStep.id,
    draft.orgOperationType,
    isSocialLogin,
    operatingProfile,
    orgOperationType,
    privacyAccepted,
    selectedBundleTiers,
    selectedClubPlanTier,
    selectedPlanTier,
    termsAccepted,
    timezone,
    isPricingFastTrack,
  ]);

  const submitOrganization = async (values: SetupFormData) => {
    const needsStarterAcknowledgment =
      isPricingFastTrack &&
      (orgOperationType === 'academy' || orgOperationType === 'both') &&
      selectedPlanTier === 'starter' &&
      !draft.starterLimitationsAcknowledged;

    if (needsStarterAcknowledgment) {
      setPendingSubmitValues(values);
      setStarterModalOpen(true);
      return;
    }

    await createOrganization(values);
  };

  const persistDetailsToDraft = () => {
    const values = getValues();
    const timezoneValue = values.timezone?.trim();
    updateDraft({
      academyName: values.academyName,
      clubName: values.clubName,
      country: values.country,
      stateOrProvince: values.stateOrProvince,
      ...(currentStep.id === 'academy-details' && timezoneValue
        ? {
            academyTimezone: timezoneValue,
            ...(orgOperationType === 'both' ? { clubTimezone: timezoneValue } : {}),
          }
        : {}),
      ...(currentStep.id === 'club-details' && timezoneValue
        ? { clubTimezone: timezoneValue }
        : {}),
    });
  };

  const createOrganization = async (
    values: SetupFormData,
    options?: { starterAcknowledgedAt?: string },
  ) => {
    const op = draft.orgOperationType ?? 'academy';

    const finalAcademyProfile =
      op !== 'club'
        ? deriveOperatingProfileFlags({
            ...operatingProfile,
            onboardingProfileCompletedAt: new Date().toISOString(),
            starterLimitationsAcknowledgedAt:
              options?.starterAcknowledgedAt ??
              (selectedPlanTier === 'starter'
                ? operatingProfile.starterLimitationsAcknowledgedAt ?? new Date().toISOString()
                : operatingProfile.starterLimitationsAcknowledgedAt),
          })
        : undefined;

    const syncedClubSports = clubSportsFromAcademyOperatingProfile(operatingProfile);
    const syncedClubLocationBand = clubLocationCountBandFromAcademyOperatingProfile(operatingProfile);

    const finalClubProfile: ClubOperatingProfileInput | undefined =
      op !== 'academy'
        ? {
            ...(clubOperatingProfile as ClubOperatingProfileInput),
            ...(syncedClubSports ? { sports: syncedClubSports } : {}),
            ...(syncedClubLocationBand ? { locationCountBand: syncedClubLocationBand } : {}),
            onboardingProfileCompletedAt: new Date().toISOString(),
          }
        : undefined;

    const legalAcceptance =
      isSocialLogin && values.termsAccepted && values.privacyAccepted
        ? {
            termsAccepted: values.termsAccepted,
            privacyAccepted: values.privacyAccepted,
            termsVersion: LEGAL_VERSIONS.TERMS,
            privacyVersion: LEGAL_VERSIONS.PRIVACY,
            marketingEmailConsent: values.marketingEmailConsent || false,
          }
        : undefined;

    const countryCode = values.country?.trim().toUpperCase();
    const stateCode = values.stateOrProvince?.trim();
    const timezone =
      values.timezone?.trim() ||
      inferTimezoneFromLocation(countryCode, stateCode) ||
      'UTC';

    let organizationResult: Awaited<ReturnType<typeof AuthService.createOrganization>>;

    if (op === 'academy') {
      if (isSocialLogin) {
        await AuthService.createAcademyForSocialUser(
          values.academyName!,
          legalAcceptance,
          countryCode,
          stateCode,
          finalAcademyProfile,
          timezone
        );
        await refreshUser();
        organizationResult = {
          organizationId: '',
          slug: '',
          name: values.academyName!,
        };
      } else {
        const academyId = await AuthService.createAcademyForUser(
          values.academyName!,
          countryCode,
          stateCode,
          finalAcademyProfile,
          timezone
        );
        await refreshUser();
        organizationResult = {
          organizationId: '',
          academyId,
          slug: '',
          name: values.academyName!,
        };
        void academyId;
      }
    } else {
      organizationResult = await AuthService.createOrganization({
        operationType: op,
        academyName: op === 'both' ? values.academyName : undefined,
        academyCountry: op === 'both' ? countryCode : undefined,
        academyStateOrProvince: op === 'both' ? stateCode : undefined,
        academyTimezone: op === 'both' ? timezone : undefined,
        academyOperatingProfile: op === 'both' ? finalAcademyProfile : undefined,
        clubName: values.clubName?.trim() || values.academyName || undefined,
        clubCountry: countryCode,
        clubStateOrProvince: stateCode,
        clubTimezone: timezone,
        clubOperatingProfile: finalClubProfile,
      });
      await refreshUser();
    }

    const refreshedUser = useAuthStore.getState().user;
    let resolvedOrganizationId = organizationResult.organizationId || draft.organizationId;

    if (!resolvedOrganizationId && refreshedUser?.academyId) {
      try {
        const academiesRes = await academyService.getUserAcademies();
        resolvedOrganizationId = academiesRes.data?.[0]?.organizationId ?? resolvedOrganizationId;
      } catch {
        // Checkout can still resolve organization server-side when omitted.
      }
    }

    if (refreshedUser?.academyId && finalAcademyProfile) {
      try {
        await academyService.updateOperatingProfile(refreshedUser.academyId, finalAcademyProfile);
      } catch {
        // Profile may already be persisted via create-organization
      }

      void tryAutoAddOwnerAsCoach({
        academyId: refreshedUser.academyId,
        user: refreshedUser,
        operatingProfile: finalAcademyProfile,
      });
    }

    const checkoutAcademyTier =
      op === 'both' ? selectedBundleTiers.academyTier : selectedPlanTier;
    const checkoutClubTier =
      op === 'both' ? selectedBundleTiers.clubTier : selectedClubPlanTier;

    if (op === 'both') {
      persistSelectedPlanTier(checkoutAcademyTier);
      persistSelectedClubPlanTier(checkoutClubTier);
    } else if (op === 'club' && checkoutClubTier) {
      persistSelectedClubPlanTier(checkoutClubTier);
    } else if (checkoutAcademyTier) {
      persistSelectedPlanTier(checkoutAcademyTier);
    }

    updateDraft({
      operatingProfile: finalAcademyProfile ?? operatingProfile,
      clubOperatingProfile: finalClubProfile ?? clubOperatingProfile,
      recommendedTier: recommendation.recommendedTier,
      recommendedClubTier: clubRecommendation.recommendedTier,
      selectedPlanTier: checkoutAcademyTier,
      selectedClubPlanTier: checkoutClubTier,
      organizationId: resolvedOrganizationId,
    });

    const entityLabel =
      op === 'club' ? 'Club' : op === 'both' ? 'Academy & club' : 'Academy';
    toast.success(`${entityLabel} created successfully!`);
    clearPricingSelection();

    const planIdForCheckout =
      op === 'club'
        ? selectedClubPlanTier
        : op === 'academy'
          ? selectedPlanTier
          : undefined;
    const finishPath = buildFinishSignupPath({ planId: planIdForCheckout });
    navigate(finishPath, {
      state: {
        email: userEmail,
        message: 'Great! Finish checkout to start your trial.',
        isSocialLogin,
        orgSetupCompleted: true,
        recommendedTier: recommendation.recommendedTier,
        recommendedClubTier: clubRecommendation.recommendedTier,
        selectedPlanTier: checkoutAcademyTier,
        selectedClubPlanTier: checkoutClubTier,
        orgOperationType: op,
        organizationId: resolvedOrganizationId,
      },
    });
  };

  const handleNext = async () => {
    if (currentStep.id === 'academy-details' || currentStep.id === 'club-details') {
      await handleSubmit(async (values) => {
        if (currentStepIndex < allSetupSteps.length - 1) {
          persistDetailsToDraft();
          setCurrentStepIndex((index) => index + 1);
          return;
        }
        try {
          setIsLoading(true);
          await submitOrganization(values);
        } catch (error: unknown) {
          const message =
            error instanceof Error ? error.message : 'Failed to create organization. Please try again.';
          setError('root', { message });
        } finally {
          setIsLoading(false);
        }
      })();
      return;
    }

    if (currentStepIndex < allSetupSteps.length - 1) {
      const completed = new Set(draft.completedStepIds ?? []);
      completed.add(currentStep.id);
      const sportIds = operatingProfile.sports?.map((s) => s.sportId);
      trackOnboardingQuestionnaireStep({
        stepId: currentStep.id,
        stepNumber: currentStepIndex + 1,
        totalSteps: allSetupSteps.length,
        sportIds,
        operationType: orgOperationType,
        flow: isPricingFastTrack ? 'pricing_fast_track' : 'questionnaire',
        sizeBand: operatingProfile.sizeBand,
        operatingRole: operatingProfile.operatingRole,
        offerings: clubOperatingProfile.offerings,
        courtCountBand: clubOperatingProfile.courtCountBand,
      });
      if (currentStep.id === 'plan' || currentStep.id === 'both-plans') {
        if (orgOperationType === 'club' && currentStep.id === 'plan') {
          trackOnboardingPlanRecommendation({
            recommendedTier: clubRecommendation.recommendedTier,
            selectedTier: selectedClubPlanTier,
            reasonCodes: clubRecommendation.reasonCodes,
          });
        } else {
          trackOnboardingPlanRecommendation({
            recommendedTier: recommendation.recommendedTier,
            selectedTier: selectedPlanTier,
            reasonCodes: recommendation.reasonCodes,
          });
        }
      }
      updateDraft({
        completedStepIds: [...completed],
        currentStepId: allSetupSteps[currentStepIndex + 1].id,
      });
      setCurrentStepIndex((index) => index + 1);
      return;
    }

    try {
      setIsLoading(true);
      const values = getValues();
      if (orgOperationType !== 'club' && !values.academyName?.trim()) {
        setError('academyName', { message: 'Academy name is required' });
        const detailsIndex = allSetupSteps.findIndex((step) => step.id === 'academy-details');
        setCurrentStepIndex(detailsIndex);
        return;
      }
      if (orgOperationType !== 'academy' && !values.clubName?.trim() && orgOperationType === 'club') {
        setError('clubName', { message: 'Club name is required' });
        const detailsIndex = allSetupSteps.findIndex((step) => step.id === 'club-details');
        setCurrentStepIndex(detailsIndex);
        return;
      }
      await submitOrganization(values);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to create organization. Please try again.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStarterModalConfirm = async () => {
    const acknowledgedAt = new Date().toISOString();
    trackStarterLimitationsAcknowledged(
      recommendation.reasonCodes.length > 0
        ? [...recommendation.reasonCodes]
        : [...STARTER_BASELINE_LIMITATIONS],
    );
    handleSelectPlanTier('starter', { starterAcknowledged: true });
    setStarterModalOpen(false);
    if (!pendingSubmitValues) return;
    try {
      setIsLoading(true);
      await createOrganization(pendingSubmitValues, { starterAcknowledgedAt: acknowledgedAt });
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to create organization. Please try again.';
      toast.error(message);
    } finally {
      setIsLoading(false);
      setPendingSubmitValues(null);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((index) => index - 1);
    }
  };

  const setupLeft = (
    <>
      <div className="flex items-center gap-2 mb-6">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900">
          <Building2 className="h-6 w-6 text-primary-600" />
        </div>
      </div>
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3">
        {orgOperationType === 'club'
          ? 'Set up your sports club'
          : orgOperationType === 'both'
            ? 'Set up your academy and club'
            : 'Set up your racquet sports academy'}
      </h1>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        {isPricingFastTrack
          ? 'Add your organization details and sports — then continue straight to checkout for your selected plan.'
          : 'Tell us how you operate so we can recommend the right plan and tailor your setup checklist.'}
      </p>
      <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
        <li className="flex gap-2">
          <span className="text-green-600 dark:text-green-400 font-semibold shrink-0">✓</span>
          <span>Tennis, pickleball, padel, and more — one platform</span>
        </li>
        <li className="flex gap-2">
          <span className="text-green-600 dark:text-green-400 font-semibold shrink-0">✓</span>
          <span>
            {isPricingFastTrack
              ? 'Plan already selected from pricing — change anytime'
              : 'Personalized plan recommendation — change anytime'}
          </span>
        </li>
        <li className="flex gap-2">
          <span className="text-green-600 dark:text-green-400 font-semibold shrink-0">✓</span>
          <span>14-day free trial on every plan</span>
        </li>
      </ul>
      {userEmail && isAuthenticated ? (
        <div className="mt-8 px-4 py-3 bg-white/80 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-lg">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Signed in as <span className="font-semibold">{userEmail}</span>
          </p>
        </div>
      ) : null}
    </>
  );

  const isFinalStep = currentStepIndex === allSetupSteps.length - 1;
  const finalLabel = 'Create and continue';

  return (
    <PlatformCheckoutShell asideVariant="onboarding" left={setupLeft}>
      <div className="mx-auto w-full max-w-2xl">
        <AcademyOnboardingWizard
          steps={allSetupSteps}
          currentStepIndex={currentStepIndex}
          onBack={handleBack}
          onNext={() => void handleNext()}
          nextDisabled={!isCurrentStepComplete}
          nextLabel={isFinalStep ? finalLabel : 'Continue'}
          isSubmitting={isLoading}
          hideProgress={isPricingFastTrack}
        >
          {isPricingFastTrack ? (
            <PricingSelectedPlanCard
              operationType={orgOperationType}
              academyTier={selectedPlanTier}
              clubTier={selectedClubPlanTier}
              billingInterval={pricingBillingInterval}
              onChangePlan={handleChangePlanFromPricing}
            />
          ) : null}
          {currentStep.id === 'operation-type' ? (
            <OrgOperationTypeStep
              value={draft.orgOperationType}
              onChange={handleOperationTypeChange}
            />
          ) : currentStep.id === 'academy-details' ? (
            <form className="space-y-4" onSubmit={(event) => event.preventDefault()}>
              <Field data-invalid={!!errors.academyName?.message}>
                <FieldLabel htmlFor="academyName">Academy name</FieldLabel>
                <Input
                  id="academyName"
                  placeholder="Enter your academy name"
                  autoComplete="organization"
                  aria-invalid={!!errors.academyName?.message}
                  {...register('academyName')}
                />
                <FieldError>{errors.academyName?.message}</FieldError>
              </Field>

              {orgOperationType === 'both' ? (
                <Field data-invalid={!!errors.clubName?.message}>
                  <FieldLabel htmlFor="clubName">Club name (optional)</FieldLabel>
                  <Input
                    id="clubName"
                    placeholder="Defaults to academy name if left blank"
                    autoComplete="organization"
                    {...register('clubName')}
                  />
                  <FieldError>{errors.clubName?.message}</FieldError>
                </Field>
              ) : null}

              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <CountrySelectField
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={errors.country?.message}
                  />
                )}
              />

              {isStateRequiredForCountry(country) && stateOptions.length > 0 ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    State / Province
                  </label>
                  <select
                    {...register('stateOrProvince')}
                    className="input"
                    aria-label="State or Province"
                  >
                    <option value="">Select state / province</option>
                    {stateOptions.map((s) => (
                      <option key={s.code} value={s.code}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {orgOperationType !== 'club' ? (
                <Controller
                  name="timezone"
                  control={control}
                  render={({ field }) => (
                    <TimezoneSelectField
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      country={country}
                      stateOrProvince={stateOrProvince}
                      id="academy-timezone"
                      error={errors.timezone?.message}
                    />
                  )}
                />
              ) : null}

              {isPricingFastTrack ? (
                <FastTrackSportsPicker
                  operationType={orgOperationType}
                  operatingProfile={operatingProfile}
                  clubOperatingProfile={clubOperatingProfile}
                  onOperatingProfileChange={handleProfileChange}
                  onClubProfileChange={handleClubProfileChange}
                />
              ) : null}

              {isSocialLogin && orgOperationType !== 'club' ? (
                <LegalAgreements register={register} errors={errors} />
              ) : null}

              {errors.root ? (
                <div className="rounded-md bg-red-50 p-4 dark:bg-red-900/20">
                  <p className="text-sm text-red-800 dark:text-red-400">{errors.root.message}</p>
                </div>
              ) : null}
            </form>
          ) : currentStep.id === 'club-details' ? (
            <form className="space-y-4" onSubmit={(event) => event.preventDefault()}>
              {orgOperationType !== 'both' ? (
                <Field data-invalid={!!errors.clubName?.message}>
                  <FieldLabel htmlFor="clubName">Club name</FieldLabel>
                  <Input
                    id="clubName"
                    placeholder="Enter your club name"
                    autoComplete="organization"
                    {...register('clubName')}
                  />
                  <FieldError>{errors.clubName?.message}</FieldError>
                </Field>
              ) : (
                <Field data-invalid={!!errors.clubName?.message}>
                  <FieldLabel htmlFor="clubName">Club name (optional)</FieldLabel>
                  <Input
                    id="clubName"
                    placeholder="Defaults to academy name if left blank"
                    autoComplete="organization"
                    {...register('clubName')}
                  />
                  <FieldError>{errors.clubName?.message}</FieldError>
                </Field>
              )}

              {orgOperationType !== 'both' ? (
                <Controller
                  name="country"
                  control={control}
                  render={({ field }) => (
                    <CountrySelectField
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={errors.country?.message}
                    />
                  )}
                />
              ) : null}

              {orgOperationType !== 'both' && isStateRequiredForCountry(country) && stateOptions.length > 0 ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    State / Province
                  </label>
                  <select
                    {...register('stateOrProvince')}
                    className="input"
                    aria-label="State or Province"
                  >
                    <option value="">Select state / province</option>
                    {stateOptions.map((s) => (
                      <option key={s.code} value={s.code}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              <Controller
                name="timezone"
                control={control}
                render={({ field }) => (
                  <TimezoneSelectField
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    country={country}
                    stateOrProvince={stateOrProvince}
                    error={errors.timezone?.message}
                  />
                )}
              />

              {isPricingFastTrack ? (
                <FastTrackSportsPicker
                  operationType={orgOperationType}
                  operatingProfile={operatingProfile}
                  clubOperatingProfile={clubOperatingProfile}
                  onOperatingProfileChange={handleProfileChange}
                  onClubProfileChange={handleClubProfileChange}
                />
              ) : null}

              {isSocialLogin ? <LegalAgreements register={register} errors={errors} /> : null}

              {errors.root ? (
                <div className="rounded-md bg-red-50 p-4 dark:bg-red-900/20">
                  <p className="text-sm text-red-800 dark:text-red-400">{errors.root.message}</p>
                </div>
              ) : null}
            </form>
          ) : currentStep.id === 'both-plans' ? (
            <CombinedOnboardingPlanSelection
              academyRecommendation={recommendation}
              clubRecommendation={clubRecommendation}
              selectedAcademyTier={selectedBundleTiers.academyTier}
              selectedClubTier={selectedBundleTiers.clubTier}
              onSelectBundle={handleSelectBundle}
              marketCountry={draft.country ?? watch('country')}
            />
          ) : currentStep.id === 'plan' ? (
            orgOperationType === 'club' ? (
              <ClubNeedsQuestionnaire
                stepId="plan"
                profile={clubOperatingProfile}
                onProfileChange={handleClubProfileChange}
                selectedPlanTier={selectedClubPlanTier}
                onSelectPlanTier={handleSelectClubPlanTier}
                marketCountry={draft.country ?? watch('country')}
              />
            ) : (
              <AcademyNeedsQuestionnaire
                stepId="plan"
                profile={operatingProfile}
                onProfileChange={handleProfileChange}
                selectedPlanTier={selectedPlanTier}
                onSelectPlanTier={handleSelectPlanTier}
                marketCountry={draft.country ?? watch('country')}
              />
            )
          ) : CLUB_QUESTIONNAIRE_STEP_IDS.includes(currentStep.id as ClubQuestionnaireStepId) ? (
            <ClubNeedsQuestionnaire
              stepId={currentStep.id as ClubQuestionnaireStepId}
              profile={clubOperatingProfile}
              onProfileChange={handleClubProfileChange}
              selectedPlanTier={selectedClubPlanTier}
              onSelectPlanTier={handleSelectClubPlanTier}
              marketCountry={draft.country ?? watch('country')}
            />
          ) : QUESTIONNAIRE_STEP_IDS.includes(currentStep.id as QuestionnaireStepId) ? (
            <AcademyNeedsQuestionnaire
              stepId={currentStep.id as QuestionnaireStepId}
              profile={operatingProfile}
              onProfileChange={handleProfileChange}
              selectedPlanTier={selectedPlanTier}
              onSelectPlanTier={handleSelectPlanTier}
              marketCountry={draft.country ?? watch('country')}
            />
          ) : null}
        </AcademyOnboardingWizard>
        <StarterLimitationsModal
          open={starterModalOpen}
          onOpenChange={setStarterModalOpen}
          limitations={[...STARTER_BASELINE_LIMITATIONS]}
          onConfirm={() => void handleStarterModalConfirm()}
        />
      </div>
    </PlatformCheckoutShell>
  );
}

function LegalAgreements({
  register,
  errors,
}: {
  register: ReturnType<typeof useForm<SetupFormData>>['register'];
  errors: ReturnType<typeof useForm<SetupFormData>>['formState']['errors'];
}) {
  return (
    <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
      <p className="text-sm font-medium text-gray-900 dark:text-white">Legal agreements</p>
      <label className="flex items-start space-x-3 cursor-pointer p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
        <input
          type="checkbox"
          {...register('termsAccepted')}
          className="mt-1 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
        />
        <div className="flex-1">
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            I agree to the{' '}
            <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-primary-600 underline">
              Terms of Service
            </Link>
          </span>
          <FieldError>{errors.termsAccepted?.message}</FieldError>
        </div>
      </label>
      <label className="flex items-start space-x-3 cursor-pointer p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
        <input
          type="checkbox"
          {...register('privacyAccepted')}
          className="mt-1 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
        />
        <div className="flex-1">
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            I agree to the{' '}
            <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-600 underline">
              Privacy Policy
            </Link>
          </span>
          <FieldError>{errors.privacyAccepted?.message}</FieldError>
        </div>
      </label>
      <label className="flex items-start space-x-3 cursor-pointer p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
        <input
          type="checkbox"
          {...register('marketingEmailConsent')}
          className="mt-1 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
        />
        <div className="flex-1 text-sm font-medium text-gray-900 dark:text-white">
          Yes, send me tips and updates to help my organization grow (optional)
        </div>
      </label>
    </div>
  );
}
